import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "react-query";
import { developAxios } from "../../axios.config";
import {
	FormLabel,
	Button,
	Input,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "@mui/material";
import axios from "axios";
import moment from "moment";

interface IEditDashboard {
	_id: string;
	name: string;
}

type TdashboardTemplates = {
	name: string;
	_id: string;
};

function EditDashboard({
	_id: dashboardId,
	name: dashboardName,
}: IEditDashboard) {
	const [courseDeadline, setCourseDeadline] = useState<string>("");
	const [name, setName] = useState<string>("");
	const [psId, setPsId] = useState<string>("");
	const [template, setTemplate] = useState<string>("");

	const collegeLogoRef = useRef<HTMLInputElement | null>(null);

	const { data } = useQuery(["superadmin", "course", dashboardId], () =>
		developAxios(localStorage.getItem("token") || "")
			.get<{
				data: {
					collegeLogo: string;
					name: string;
					projectSpecificationOrder: string;
					dashboardTemplate: string;
					courseDeadline: string;
				};
			}>("/superadmin/course/" + dashboardId)
			.then(({ data }) => data.data)
	);

	const { data: projectSpecifications } = useQuery(
		["get", "project-specificiations"],
		() => {
			return developAxios(localStorage.getItem("token") || "")
				.get<{ data: { _id: string; name: string }[] }>(
					"/superadmin/project-specification"
				)
				.then(({ data }) => data.data);
		}
	);

	const { data: dashboardTemplates } = useQuery(
		["get", "dashboard-templates"],
		() => {
			return developAxios(localStorage.getItem("token") || "")
				.get<{ data: TdashboardTemplates[] }>(
					"/superadmin/course/dashboard-templates"
				)
				.then(({ data }) => data.data);
		}
	);

	const { mutateAsync: uploadImage, data: uploadImageData } = useMutation(
		async (logo: File) => {
			if (!logo) return;
			const name_arr = logo.name.split(".");
			const type = name_arr[name_arr.length - 1];

			const { data } = await developAxios(
				localStorage.getItem("token") || ""
			).get<{
				data: { presignedUrl: string; url: string };
			}>("/superadmin/course/college-logo-presigned-url", {
				params: {
					fileType: type || "png",
				},
			});
			await axios.put(data.data.presignedUrl, logo);

			return data.data.url;
		}
	);

	const { mutateAsync: updateData } = useMutation(() => {
		return developAxios(localStorage.getItem("token") || "")
			.post("/superadmin/course/" + dashboardId, {
				name,
				courseDeadline,
				collegeLogo: uploadImageData,
				projectSpecificationOrder: psId,
				dashboardTemplate: template,
			})
			.then(({ data }) => data.data);
	});

	useEffect(() => {
		if (!data) return;

		setName(data.name);
		const deadline = moment(data.courseDeadline).format("YYYY-MM-DD");
		setCourseDeadline(`${deadline}`);
		setTemplate(data.dashboardTemplate);
		setPsId(data.projectSpecificationOrder);
	}, [data]);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		updateData();
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="flex gap-4 items-center">
				<FormLabel htmlFor="name">Dashboard Name</FormLabel>
				<Input
					id="name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					required
				/>
			</div>
			<div className="flex gap-4 items-center">
				<FormLabel htmlFor="college_logo">Dashboard Logo</FormLabel>
				<input
					id="college_logo"
					type="file"
					ref={collegeLogoRef}
					onChange={(e) =>
						e.target.files && uploadImage(e.target.files[0])
					}
					required
					accept="image/*"
				/>
				{data && data.collegeLogo && (
					<div className="p-2 border border-black">
						<img
							src={uploadImageData || data.collegeLogo}
							alt="College log preview"
							className="w-60 h-auto"
						/>
					</div>
				)}
			</div>
			<div className="flex gap-4 items-center">
				<label htmlFor="courseDeadLine">Course Deadline</label>
				<input
					type="date"
					onChange={(e) => setCourseDeadline(e.target.value)}
					value={courseDeadline}
				/>
			</div>
			<div className="flex gap-4 items-center">
				<FormControl fullWidth>
					<InputLabel id="dashboard-templates-dropdown">
						Select the dashboard template
					</InputLabel>
					<Select
						onChange={(e) => setTemplate(e.target.value as string)}
						value={template}
						required
						label="Select the dashboard template"
						className="w-80"
						labelId="dashboard-templates-dropdown"
					>
						{dashboardTemplates &&
							dashboardTemplates.map((val) => (
								<MenuItem key={val._id} value={val._id}>
									{val.name}
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</div>
			<div className="flex gap-4 items-center">
				<FormControl fullWidth>
					<InputLabel id="project-specification-templates-dropdown">
						Select the Project Specification ID
					</InputLabel>
					<Select
						onChange={(e) => setPsId(e.target.value as string)}
						value={psId}
						label="Select the ProjectSpefication ID"
						className="w-80"
						labelId="project-specification-templates-dropdown"
					>
						{projectSpecifications &&
							projectSpecifications.map((val) => (
								<MenuItem key={val._id} value={val._id}>
									{val.name}
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</div>
			<Button variant="contained" color="primary" type="submit">
				Save Changes
			</Button>
		</form>
	);
}

export default EditDashboard;
